import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import {
  CircularProgress,
  DialogContentText,
  Typography,
  Grow,
} from '@material-ui/core'

import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Dialog from '@material-ui/core/Dialog'
import { useTranslation } from 'react-i18next'
import Button from 'ui/Button'
import parseError from 'helpers/errorHelpers'

const useStyles = makeStyles((theme) => ({
  wrapper: {},
  label: { textAlign: 'center' },
  content: {
    padding: theme.spacing(2),
  },
  actions: {
    padding: theme.spacing(2, 0),
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'space-evenly',
  },
  button: {
    padding: theme.spacing(0.5, 0),
    minWidth: '120px',
  },
  paper: {
    padding: theme.spacing(1),
    border: `2px solid ${theme.palette.primary.main}`,
    display: 'flex',
    flexFlow: 'column nowrap',
    justifyContent: 'space-between',
  },
}))

const GrowTransition = React.forwardRef(function Transition(props, ref) {
  return <Grow ref={ref} {...props} />
})

export default function BaseDialog({
  onClose = () => {},
  open = false,
  isError = false,
  error,
  isLoading = false,
  disabled = false,
  label,
  content,
  onSubmit = () => {},
  hideSecondaryAction = false,
  hidePrimaryAction = false,
  primaryActionProps = {},
  secondaryActionProps = {},
  ...other
}) {
  const classes = useStyles()
  const { t } = useTranslation()
  const handleSubmit = () => {
    onSubmit()
  }

  return (
    <Dialog
      maxWidth="xs"
      open={open}
      onClose={onClose}
      scroll={'paper'}
      TransitionComponent={GrowTransition}
      {...other}
    >
      <DialogTitle color={'primary'} className={classes.label}>
        {label}
      </DialogTitle>
      <DialogContent>
        {content ? content : null}
        {isError ? (
          <DialogContentText>
            <Typography
              display="block"
              align="center"
              variant="body1"
              color="error"
            >
              {parseError(error, 'Default Error')}
            </Typography>
          </DialogContentText>
        ) : null}
      </DialogContent>
      <DialogActions className={classes.actions}>
        {hideSecondaryAction ? null : (
          <Button
            variant="outlined"
            onClick={onClose}
            color={'primary'}
            rounded
            className={classes.button}
            {...secondaryActionProps}
          >
            {t('common.actions.back')}
          </Button>
        )}

        {hidePrimaryAction ? null : (
          <Button
            disabled={isLoading || disabled}
            variant="contained"
            onClick={handleSubmit}
            color={'red'}
            rounded
            className={classes.button}
            {...primaryActionProps}
          >
            {isLoading ? (
              <CircularProgress size={24} />
            ) : primaryActionProps && primaryActionProps.label ? (
              primaryActionProps.label
            ) : (
              t('common.actions.submit')
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

BaseDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  isError: PropTypes.bool,
  error: PropTypes.any,
  label: PropTypes.string,
  content: PropTypes.any,
  renderContent: PropTypes.func,
  contentTypographyProps: PropTypes.object,
  onSubmit: PropTypes.func,
  hidePrimaryAction: PropTypes.bool,
  hideSecondaryAction: PropTypes.bool,
  primaryActionProps: PropTypes.object,
  secondaryActionProps: PropTypes.object,
}
